// React imports
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  GraduationCap,
  Users,
  Globe,
  Award,
  BookOpen,
  Target,
  ChevronRight,
  CheckCircle,
  Calendar,
  ArrowRight,
  Star,
  MapPin,
  Mail,
  Search,
  Frown,
  Smile,
  Lightbulb,
  Sparkles,
  PersonStanding,
  Laptop,
  Clock,
  ThumbsUp,
  Zap,
  Heart,
  Rocket,
  CheckCheck,
  MousePointer,
  Compass,
  Puzzle,
  Crown,
  Cpu,
  FileText,
  // Using alternative icons for social media
  ExternalLink,
  MessageCircle
} from "lucide-react";

const About = () => {
  // Stats are defined directly in the JSX for better maintainability

  // Core values are defined directly in the JSX for better maintainability

  const team = [
    {
      name: "Sarah Johnson",
      role: "CEO & Founder",
      bio: "Former education consultant with a passion for accessible learning.",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop",
      education: "MBA, Harvard Business School",
      experience: "15+ years in EdTech",
      quote: "Education is the most powerful tool we can use to change the world.",
      achievements: ["Forbes 30 Under 30 in Education", "EdTech Visionary Award 2023"],
      skills: ["Leadership", "Strategic Planning", "Educational Design"]
    },
    {
      name: "Michael Chen",
      role: "Chief Technology Officer",
      bio: "Tech innovator with 15+ years experience in educational platforms.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop",
      education: "Ph.D. in Computer Science, MIT",
      experience: "Previously at Google & Khan Academy",
      quote: "Technology should remove barriers to education, not create them.",
      achievements: ["Developed AI learning algorithm", "12 tech patents"],
      skills: ["AI/ML", "Platform Architecture", "EdTech Innovation"]
    },
    {
      name: "Emily Roberts",
      role: "Head of Content",
      bio: "Curriculum expert dedicated to creating engaging learning materials.",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop",
      education: "M.Ed. in Curriculum Development",
      experience: "10+ years in educational publishing",
      quote: "Great content doesn't just inform—it inspires and transforms.",
      achievements: ["Authored bestselling textbooks", "Educational Content Award"],
      skills: ["Curriculum Design", "Content Strategy", "Educational Psychology"]
    },
    {
      name: "David Wilson",
      role: "Chief Learning Officer",
      bio: "Educational psychologist focused on optimizing learning experiences.",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop",
      education: "Ph.D. in Educational Psychology",
      experience: "Former university professor & researcher",
      quote: "Understanding how people learn is the key to effective education.",
      achievements: ["Published 25+ research papers", "Learning Science Innovation Award"],
      skills: ["Learning Analytics", "Educational Research", "Cognitive Psychology"]
    },
    {
      name: "Sophia Martinez",
      role: "VP of Partnerships",
      bio: "Building strategic relationships with educational institutions worldwide.",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop",
      education: "MBA, Stanford University",
      experience: "Former Business Development at Coursera",
      quote: "Partnerships multiply our impact and reach.",
      achievements: ["Secured 100+ university partnerships", "Global Education Alliance Award"],
      skills: ["Relationship Building", "Strategic Partnerships", "Business Development"]
    },
    {
      name: "James Taylor",
      role: "UX Design Director",
      bio: "Creating intuitive and accessible learning experiences for all users.",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop",
      education: "MFA in Interaction Design",
      experience: "Previously led design at top EdTech startups",
      quote: "Great design removes friction from the learning journey.",
      achievements: ["Multiple UX Design Awards", "Accessibility Champion Recognition"],
      skills: ["User Experience", "Accessibility", "Educational Interface Design"]
    }
  ];

  const whyUsReasons = [
    {
      icon: Lightbulb,
      title: "Personalized Learning",
      description: "Our AI-powered system matches you with courses that fit your unique learning style and career goals."
    },
    {
      icon: Clock,
      title: "Learn at Your Pace",
      description: "Flexible schedules and on-demand content let you learn whenever and wherever works best for you."
    },
    {
      icon: Users,
      title: "Expert Instructors",
      description: "Learn from industry professionals with real-world experience and proven teaching methods."
    },
    {
      icon: ThumbsUp,
      title: "Guaranteed Satisfaction",
      description: "With our 30-day money-back guarantee, you can try any course risk-free."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-700 opacity-90"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00eiIvPjxwYXRoIGQ9Ik0xNiAxNmMyLjIgMCA0IDEuOCA0IDRzLTEuOCA0LTQgNC00LTEuOC00LTQgMS44LTQgNC00em0xNiAwYzIuMiAwIDQgMS44IDQgNHMtMS44IDQtNCA0LTQtMS44LTQtNCAxLjgtNCA0LTR6TTM2IDE2YzIuMiAwIDQgMS44IDQgNHMtMS44IDQtNCA0LTQtMS44LTQtNCAxLjgtNCA0LTR6TTYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNGMwIDIuMi0xLjggNC00IDRzLTQtMS44LTQtNHptMTYgMGMwLTIuMiAxLjgtNCA0LTRzNCAxLjggNCA0YzAgMi4yLTEuOCA0LTQgNC00LTEuOC00LTR6TTYgMTZjMi4yIDAgNCAxLjggNCA0cy0xLjggNC00IDQtNC0xLjgtNC00IDEuOC00IDQtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-10"></div>

        <div className="container relative z-10 mx-auto px-4 pt-32 pb-24 md:pt-40 md:pb-32">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-white/20 text-white hover:bg-white/30 border-none px-4 py-1.5 text-sm">
              Our Story
            </Badge>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white animate-fadeIn">
              Empowering Future Leaders Through Education
            </h1>

            <p className="mt-6 text-lg text-white/80 animate-fadeIn" style={{ animationDelay: '200ms' }}>
              LearnHub is dedicated to making quality education accessible to everyone.
              We believe in the power of knowledge to transform lives and create positive change.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center animate-fadeIn" style={{ animationDelay: '400ms' }}>
              <Button size="lg" className="flex items-center gap-2 bg-white text-primary hover:bg-white/90">
                Our Courses <ChevronRight size={16} />
              </Button>
              <Button size="lg" className="flex items-center gap-2 bg-white text-primary hover:bg-white/90">
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-b from-white to-blue-50/20 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-72 h-72 bg-primary/5 rounded-full opacity-50"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-100/20 rounded-full opacity-40"></div>

        <div className="container mx-auto max-w-6xl px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="mb-2">
              <Badge className="bg-primary/20 text-primary hover:bg-primary/30 border-none text-base px-4 py-1.5 font-medium">Our Impact</Badge>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 relative inline-block">LearnHub by the Numbers
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-primary/30 rounded-full"></div>
            </h2>
            <p className="text-lg text-muted-foreground mt-6">Our growing community of learners, instructors, and courses is making a real difference in education worldwide.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6">
            {[
              {
                number: "10K+",
                label: "Active Learners",
                icon: Users,
                description: "Students actively engaged in our courses",
                color: "blue",
                growth: "+27% this year"
              },
              {
                number: "500+",
                label: "Expert Instructors",
                icon: GraduationCap,
                description: "Industry professionals sharing their expertise",
                color: "purple",
                growth: "+48 joined recently"
              },
              {
                number: "1000+",
                label: "Courses Available",
                icon: BookOpen,
                description: "Comprehensive courses across various disciplines",
                color: "amber",
                growth: "+120 added this quarter"
              },
              {
                number: "95%",
                label: "Success Rate",
                icon: Award,
                description: "Students who successfully complete their courses",
                color: "green",
                growth: "Consistently above industry average"
              }
            ].map((stat, index) => (
              <div
                key={stat.label}
                className={`animate-fadeIn bg-white rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-500 border border-muted/10 group relative overflow-hidden`}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Top accent bar */}
                <div className={`absolute top-0 left-0 w-full h-1 ${stat.color === 'blue' ? 'bg-blue-500/70' : stat.color === 'purple' ? 'bg-purple-500/70' : stat.color === 'amber' ? 'bg-amber-500/70' : 'bg-green-500/70'}`}></div>

                <div className="relative z-10">
                  <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 border ${stat.color === 'blue' ? 'bg-blue-50 border-blue-200' : stat.color === 'purple' ? 'bg-purple-50 border-purple-200' : stat.color === 'amber' ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                    <stat.icon className={`h-10 w-10 ${stat.color === 'blue' ? 'text-blue-500' : stat.color === 'purple' ? 'text-purple-500' : stat.color === 'amber' ? 'text-amber-500' : 'text-green-500'}`} />
                  </div>

                  <div className="relative">
                    <div className="text-4xl font-bold mb-2 group-hover:scale-110 transition-transform duration-300 inline-block">
                      <span className={`${stat.color === 'blue' ? 'text-blue-600' : stat.color === 'purple' ? 'text-purple-600' : stat.color === 'amber' ? 'text-amber-600' : 'text-green-600'}`}>{stat.number}</span>
                    </div>
                    <div className="text-base font-semibold mb-3">{stat.label}</div>
                    <p className="text-sm text-muted-foreground mb-4">{stat.description}</p>

                    <div className={`text-xs font-medium py-1 px-3 rounded-full inline-block mb-2 ${stat.color === 'blue' ? 'bg-blue-50 text-blue-600' : stat.color === 'purple' ? 'bg-purple-50 text-purple-600' : stat.color === 'amber' ? 'bg-amber-50 text-amber-600' : 'bg-green-50 text-green-600'}`}>
                      {stat.growth}
                    </div>
                  </div>
                </div>

                {/* Decorative corner shape */}
                <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-muted/5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700"></div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Button variant="outline" className="flex items-center gap-2 mx-auto">
              View Detailed Analytics <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-gradient-to-b from-blue-50/30 to-white relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-20 left-10 w-40 h-40 bg-primary/5 rounded-full animate-float opacity-70"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-indigo-100/30 rounded-full animate-float opacity-50" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-amber-100/20 rounded-full animate-float opacity-40" style={{ animationDelay: '0.8s' }}></div>

        <div className="container mx-auto max-w-6xl px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="mb-2">
              <Badge className="bg-primary/20 text-primary hover:bg-primary/30 border-none text-base px-4 py-1.5 font-medium">Our Purpose</Badge>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 relative inline-block">Our Mission
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-primary/30 rounded-full"></div>
            </h2>
            <p className="text-lg text-muted-foreground mt-6">We're on a journey to transform education and empower learners worldwide through accessible, high-quality learning experiences.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeIn bg-white p-8 rounded-xl shadow-lg border border-muted/10 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/40 via-primary/60 to-primary/40"></div>

              <div className="space-y-6 mb-8 relative z-10">
                {[
                  {
                    icon: Target,
                    title: "Revolutionize Education",
                    description: "Transform online education by providing accessible, high-quality learning experiences for everyone."
                  },
                  {
                    icon: Rocket,
                    title: "Empower Individuals",
                    description: "Help people achieve their full potential through personalized, continuous learning opportunities."
                  },
                  {
                    icon: Users,
                    title: "Build Global Community",
                    description: "Create a worldwide network of learners and educators dedicated to knowledge sharing and growth."
                  },
                  {
                    icon: Heart,
                    title: "Create Social Impact",
                    description: "Drive positive change through education initiatives in underserved communities around the world."
                  }
                ].map((item, index) => (
                  <div key={item.title} className="flex items-start gap-4 group animate-fadeIn" style={{ animationDelay: `${index * 150}ms` }}>
                    <div className="mt-1 bg-primary/10 p-3 rounded-full group-hover:bg-primary/20 transition-colors duration-300 flex-shrink-0">
                      <item.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1 group-hover:text-primary transition-colors duration-300">{item.title}</h3>
                      <p className="text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <Button size="lg" className="flex items-center gap-2 px-6 shadow-md">
                  Join Our Community <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button size="lg" variant="outline" className="flex items-center gap-2">
                  Learn More <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Decorative corner shape */}
              <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-primary/5 rounded-full"></div>
            </div>

            <div className="relative animate-fadeIn" style={{ animationDelay: '200ms' }}>
              {/* Main image */}
              <div className="rounded-xl overflow-hidden shadow-xl transform hover:scale-[1.02] transition-transform duration-500 relative z-20">
                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&auto=format&fit=crop"
                  alt="Students collaborating"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-70"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-xl font-bold mb-2">Transforming Lives Through Education</h3>
                  <p className="text-white/80 text-sm">Our mission in action - students collaborating and learning together</p>
                </div>
              </div>

              {/* Floating smaller images */}
              <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-lg overflow-hidden shadow-lg border-4 border-white z-10 animate-float" style={{ animationDelay: '0.5s' }}>
                <img
                  src="https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=300&auto=format&fit=crop"
                  alt="Educational resources"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="absolute -top-6 -right-6 w-40 h-40 rounded-lg overflow-hidden shadow-lg border-4 border-white z-10 animate-float" style={{ animationDelay: '1s' }}>
                <img
                  src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=300&auto=format&fit=crop"
                  alt="Global education"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Decorative elements */}
              <div className="absolute -bottom-3 right-1/4 transform translate-x-1/2 z-30">
                <div className="bg-white p-2 rounded-full shadow-lg">
                  <Star className="h-6 w-6 text-amber-500 fill-amber-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Mission stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
            {[
              { number: "10K+", label: "Active Learners", color: "from-blue-50 to-blue-100/50" },
              { number: "150+", label: "Countries Reached", color: "from-green-50 to-green-100/50" },
              { number: "95%", label: "Satisfaction Rate", color: "from-amber-50 to-amber-100/50" },
              { number: "500+", label: "Community Partners", color: "from-purple-50 to-purple-100/50" }
            ].map((stat, index) => (
              <div
                key={stat.label}
                className={`bg-gradient-to-br ${stat.color} rounded-xl p-6 text-center shadow-sm border border-muted/10 hover:shadow-md transition-all duration-300 animate-fadeIn group`}
                style={{ animationDelay: `${index * 100 + 400}ms` }}
              >
                <div className="text-3xl font-bold text-primary mb-1 group-hover:scale-110 transition-transform duration-300">{stat.number}</div>
                <div className="text-sm text-muted-foreground group-hover:text-primary/80 transition-colors duration-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gradient-to-b from-white to-blue-50/30 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-40 right-20 w-64 h-64 bg-primary/5 rounded-full animate-float opacity-50" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute bottom-20 left-10 w-40 h-40 bg-indigo-100/20 rounded-full animate-float opacity-40" style={{ animationDelay: '1.2s' }}></div>
        <div className="absolute top-1/3 left-1/3 w-20 h-20 bg-amber-100/10 rounded-full animate-float opacity-30" style={{ animationDelay: '0.8s' }}></div>

        <div className="container mx-auto max-w-6xl px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="mb-2">
              <Badge className="bg-primary/20 text-primary hover:bg-primary/30 border-none text-base px-4 py-1.5 font-medium">What We Stand For</Badge>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 relative inline-block">Our Core Values
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-primary/30 rounded-full"></div>
            </h2>
            <p className="text-lg text-muted-foreground mt-6">
              These fundamental principles guide everything we do and shape our approach to education and learning.
            </p>
          </div>

          {/* Interactive Values Display */}
          <div className="relative mb-16">
            <div className="absolute inset-0 flex items-center justify-center opacity-10">
              <div className="w-[500px] h-[500px] border-2 border-dashed border-primary/30 rounded-full animate-spin-slow"></div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
              {[
                {
                  icon: BookOpen,
                  title: "Quality Education",
                  description: "We believe in providing top-tier educational content that transforms lives.",
                  color: "blue",
                  quote: "Education is not the filling of a pail, but the lighting of a fire."
                },
                {
                  icon: Users,
                  title: "Community First",
                  description: "Building a supportive community of learners and educators worldwide.",
                  color: "purple",
                  quote: "Alone we can do so little; together we can do so much."
                },
                {
                  icon: Target,
                  title: "Goal-Oriented",
                  description: "Focused on helping our students achieve their career and personal goals.",
                  color: "amber",
                  quote: "A goal without a plan is just a wish."
                },
                {
                  icon: Award,
                  title: "Excellence",
                  description: "Committed to maintaining the highest standards in online education.",
                  color: "green",
                  quote: "Excellence is not a skill. It's an attitude."
                }
              ].map((value, index) => (
                <div
                  key={value.title}
                  className={`bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-500 border border-muted/10 group animate-fadeIn relative overflow-hidden`}
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  {/* Top accent bar */}
                  <div className={`absolute top-0 left-0 w-full h-1 ${value.color === 'blue' ? 'bg-blue-500/70' : value.color === 'purple' ? 'bg-purple-500/70' : value.color === 'amber' ? 'bg-amber-500/70' : 'bg-green-500/70'}`}></div>

                  {/* Icon with colored background */}
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 border ${value.color === 'blue' ? 'bg-blue-50 border-blue-200' : value.color === 'purple' ? 'bg-purple-50 border-purple-200' : value.color === 'amber' ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                    <value.icon className={`w-10 h-10 ${value.color === 'blue' ? 'text-blue-500' : value.color === 'purple' ? 'text-purple-500' : value.color === 'amber' ? 'text-amber-500' : 'text-green-500'}`} />
                  </div>

                  <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">{value.title}</h3>
                  <p className="text-muted-foreground group-hover:text-foreground/90 transition-colors duration-300 mb-4">{value.description}</p>

                  {/* Quote that appears on hover */}
                  <div className="mt-4 h-0 overflow-hidden opacity-0 group-hover:h-auto group-hover:opacity-100 transition-all duration-500 ease-in-out">
                    <div className="pt-4 border-t border-muted/20">
                      <p className="text-sm italic text-muted-foreground">"{value.quote}"</p>
                    </div>
                  </div>

                  {/* Decorative corner shape */}
                  <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-muted/5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Values in Action */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-muted/10">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-8 md:p-12">
                <h3 className="text-2xl font-bold mb-6">Our Values in Action</h3>
                <p className="text-muted-foreground mb-6">
                  We don't just talk about our values—we live them every day. Here's how our core principles translate into tangible benefits for our learners:
                </p>

                <div className="space-y-6">
                  {[
                    {
                      title: "Rigorous Quality Assurance",
                      description: "Every course undergoes a thorough review process to ensure it meets our high standards."
                    },
                    {
                      title: "Supportive Learning Environment",
                      description: "Our community forums and mentorship programs ensure no learner feels alone on their journey."
                    },
                    {
                      title: "Career-Focused Curriculum",
                      description: "Courses are designed with real-world applications and industry needs in mind."
                    },
                    {
                      title: "Continuous Improvement",
                      description: "We regularly update our content and platform based on learner feedback and educational research."
                    }
                  ].map((item, i) => (
                    <div key={item.title} className="flex items-start gap-3 group animate-fadeIn" style={{ animationDelay: `${i * 150 + 600}ms` }}>
                      <div className="mt-1 bg-primary/10 p-1.5 rounded-full group-hover:bg-primary/20 transition-colors duration-300">
                        <CheckCircle className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-bold text-base mb-1 group-hover:text-primary transition-colors duration-300">{item.title}</h4>
                        <p className="text-sm text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Button className="mt-8 flex items-center gap-2">
                  Learn About Our Approach <ArrowRight className="h-4 w-4" />
                </Button>
              </div>

              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&auto=format&fit=crop"
                  alt="Team collaboration"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-indigo-600/90 opacity-90 mix-blend-multiply"></div>

                <div className="absolute inset-0 flex items-center justify-center p-8">
                  <div className="text-center text-white max-w-md">
                    <h3 className="text-2xl font-bold mb-4">Join a Community That Cares</h3>
                    <p className="text-white/90 mb-6">
                      Experience the difference of learning with an organization that puts values at the heart of everything we do.
                    </p>
                    <Button size="lg" className="flex items-center gap-2 bg-white text-primary hover:bg-white/90">
                    Join Our Community
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-gradient-to-b from-blue-50/30 to-white relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-20 right-10 w-40 h-40 bg-primary/5 rounded-full animate-float opacity-70"></div>
        <div className="absolute bottom-20 left-10 w-32 h-32 bg-indigo-100/30 rounded-full animate-float opacity-50" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute top-1/3 right-1/4 w-16 h-16 bg-amber-100/20 rounded-full animate-float opacity-40" style={{ animationDelay: '0.8s' }}></div>

        <div className="container mx-auto max-w-6xl px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="mb-2">
              <Badge className="bg-primary/20 text-primary hover:bg-primary/30 border-none text-base px-4 py-1.5 font-medium">Meet Our Team</Badge>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 relative inline-block">The People Behind LearnHub
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-primary/30 rounded-full"></div>
            </h2>
            <p className="text-lg text-muted-foreground mt-6">
              Our diverse team of education experts, technologists, and visionaries working together to transform learning.
            </p>
          </div>

          {/* Leadership Team */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold mb-8 text-center">Leadership Team</h3>
            <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
              {team.slice(0, 2).map((member, index) => (
                <div
                  key={member.name}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-500 group animate-fadeIn flex flex-col md:flex-row gap-6 items-center md:items-start relative overflow-hidden"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  {/* Decorative elements */}
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/40 via-primary/60 to-primary/40"></div>
                  <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-primary/5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700"></div>

                  <div className="relative">
                    <div className="relative w-32 h-32 rounded-full overflow-hidden border-4 border-primary/10 group-hover:border-primary transition-colors duration-500 shadow-md">
                      <img src={member.image} alt={member.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </div>
                    <div className="absolute -bottom-2 -right-2 bg-primary text-white p-1.5 rounded-full shadow-md">
                      {index === 0 ? (
                        <Crown className="h-5 w-5" />
                      ) : (
                        <Cpu className="h-5 w-5" />
                      )}
                    </div>
                  </div>

                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-2xl font-bold mb-2 group-hover:text-primary transition-colors">{member.name}</h3>
                    <p className="text-primary font-medium mb-4 inline-block relative">
                      {member.role}
                      <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary/20 group-hover:bg-primary/40 transition-colors duration-300"></span>
                    </p>
                    <p className="text-muted-foreground mb-4">{member.bio}</p>

                    <div className="flex gap-3 justify-center md:justify-start">
                      <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 bg-muted/20 hover:bg-primary/10 hover:text-primary">
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 bg-muted/20 hover:bg-primary/10 hover:text-primary">
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 bg-muted/20 hover:bg-primary/10 hover:text-primary">
                        <Mail className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Core Team */}
          <div>
            <h3 className="text-2xl font-bold mb-8 text-center">Core Team</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {team.slice(2).map((member, index) => (
                <div
                  key={member.name}
                  className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-500 text-center group animate-fadeIn relative overflow-hidden"
                  style={{ animationDelay: `${(index + 2) * 150}ms` }}
                >
                  {/* Decorative elements */}
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/40 via-primary/60 to-primary/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-primary/5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700"></div>

                  <div className="relative inline-block">
                    <div className="w-28 h-28 mx-auto rounded-full overflow-hidden border-4 border-primary/10 group-hover:border-primary transition-colors duration-500 shadow-md">
                      <img src={member.image} alt={member.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 bg-primary/80 text-white p-1 rounded-full shadow-md">
                      {index === 0 ? (
                        <FileText className="h-4 w-4" />
                      ) : (
                        <Lightbulb className="h-4 w-4" />
                      )}
                    </div>
                  </div>

                  <h3 className="text-xl font-bold mt-4 mb-1 group-hover:text-primary transition-colors">{member.name}</h3>
                  <p className="text-primary font-medium mb-3 inline-block relative">
                    {member.role}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary/20 group-hover:w-full transition-all duration-500"></span>
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">{member.bio}</p>

                  <div className="flex gap-2 justify-center">
                    <Button variant="ghost" size="icon" className="rounded-full h-7 w-7 bg-muted/20 hover:bg-primary/10 hover:text-primary">
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="rounded-full h-7 w-7 bg-muted/20 hover:bg-primary/10 hover:text-primary">
                      <Mail className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Join Our Team CTA */}
          <div className="mt-16 bg-white rounded-xl shadow-lg p-8 border border-muted/10 max-w-3xl mx-auto relative overflow-hidden">
            <div className="absolute -top-12 -left-12 w-40 h-40 bg-primary/5 rounded-full"></div>
            <div className="absolute -bottom-12 -right-12 w-40 h-40 bg-blue-50 rounded-full"></div>

            <div className="relative z-10 text-center">
              <Users className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-3">Join Our Growing Team</h3>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                We're always looking for talented individuals who are passionate about education and technology.
                Check out our current openings and become part of our mission to transform learning.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button className="px-6 shadow-md">
                  View Open Positions <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button variant="outline">
                  Learn About Our Culture
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/20 border-none">Our Difference</Badge>
            <h2 className="text-3xl font-bold mb-4">Why Us</h2>
            <p className="text-lg text-muted-foreground">
              Discover what makes our platform the ideal choice for your learning journey.
            </p>
          </div>

          {/* Animation Story */}
          <div className="mb-20 max-w-5xl mx-auto">
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 p-8 shadow-lg border border-primary/10 animate-shimmer">
              <div className="absolute -top-10 -left-10 w-20 h-20 bg-blue-100 rounded-full opacity-20 animate-float"></div>
              <div className="absolute -bottom-10 -right-10 w-16 h-16 bg-indigo-100 rounded-full opacity-20 animate-float" style={{ animationDelay: '1s' }}></div>

              <h3 className="text-2xl font-bold mb-8 text-center relative">
                <span className="relative inline-block">
                  Find the Perfect Course for You
                  <span className="absolute bottom-0 left-0 w-full h-1 bg-primary/20 rounded animate-scaleIn" style={{ transformOrigin: 'left', animationDelay: '0.5s' }}></span>
                </span>
              </h3>

              {/* Animation Sequence */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
                {/* Step 1: Struggling */}
                <div className="group bg-white rounded-lg p-6 shadow-md transform transition-all duration-500 hover:shadow-xl hover:-translate-y-1 animate-scaleIn cursor-pointer overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-red-50 to-red-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 border border-red-200">
                      <Frown className="h-10 w-10 text-red-500 group-hover:animate-pulse" />
                    </div>
                    <h4 className="text-xl font-bold mb-3 text-center group-hover:text-red-600 transition-colors duration-300">The Challenge</h4>
                    <p className="text-muted-foreground text-center mb-4">
                      Feeling overwhelmed by too many options and struggling to find the right course for your needs.
                    </p>
                    <div className="mt-4 flex justify-center">
                      <div className="relative w-24 h-24">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <PersonStanding className="h-14 w-14 text-slate-700" />
                        </div>
                        <div className="absolute top-0 right-0 animate-ping">
                          <Search className="h-7 w-7 text-slate-400" />
                        </div>
                        <div className="absolute bottom-0 left-0 animate-ping" style={{ animationDelay: '1s' }}>
                          <Search className="h-5 w-5 text-slate-400" />
                        </div>
                        <div className="absolute top-1/4 left-1/4 animate-ping" style={{ animationDelay: '1.5s' }}>
                          <Compass className="h-6 w-6 text-slate-400" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 2: Discovery */}
                <div className="group bg-white rounded-lg p-6 shadow-md transform transition-all duration-500 hover:shadow-xl hover:-translate-y-1 animate-scaleIn cursor-pointer overflow-hidden" style={{ animationDelay: '300ms' }}>
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-50 to-amber-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="w-20 h-20 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 border border-amber-200">
                      <Lightbulb className="h-10 w-10 text-amber-500 group-hover:animate-pulse" />
                    </div>
                    <h4 className="text-xl font-bold mb-3 text-center group-hover:text-amber-600 transition-colors duration-300">The Discovery</h4>
                    <p className="text-muted-foreground text-center mb-4">
                      Finding our platform that uses smart technology to match you with personalized learning options.
                    </p>
                    <div className="mt-4 flex justify-center">
                      <div className="relative w-24 h-24">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <PersonStanding className="h-14 w-14 text-slate-700" />
                        </div>
                        <div className="absolute top-0 left-0 animate-bounce">
                          <Laptop className="h-8 w-8 text-primary" />
                        </div>
                        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                          <Zap className="h-6 w-6 text-amber-500 animate-pulse" />
                        </div>
                        <div className="absolute bottom-0 right-0">
                          <MousePointer className="h-5 w-5 text-primary animate-bounce" style={{ animationDelay: '0.5s' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 3: Solution */}
                <div className="group bg-white rounded-lg p-6 shadow-md transform transition-all duration-500 hover:shadow-xl hover:-translate-y-1 animate-scaleIn cursor-pointer overflow-hidden" style={{ animationDelay: '600ms' }}>
                  <div className="absolute inset-0 bg-gradient-to-r from-green-50 to-green-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 border border-green-200">
                      <Smile className="h-10 w-10 text-green-500 group-hover:animate-pulse" />
                    </div>
                    <h4 className="text-xl font-bold mb-3 text-center group-hover:text-green-600 transition-colors duration-300">The Perfect Match</h4>
                    <p className="text-muted-foreground text-center mb-4">
                      <span className="font-medium text-primary inline-block relative group-hover:text-green-600 transition-colors duration-300">
                        "Cours selon ses besoins"
                        <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary/30 group-hover:bg-green-500/50 transition-colors duration-300"></span>
                      </span>
                      <span className="block mt-1">Finding courses perfectly tailored to your unique needs and goals.</span>
                    </p>
                    <div className="mt-4 flex justify-center">
                      <div className="relative w-24 h-24">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <PersonStanding className="h-14 w-14 text-slate-700" />
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Sparkles className="h-20 w-20 text-amber-400 opacity-70 animate-pulse" />
                        </div>
                        <div className="absolute top-0 right-0">
                          <CheckCheck className="h-6 w-6 text-green-500 animate-bounce" />
                        </div>
                        <div className="absolute bottom-0 left-0">
                          <Heart className="h-5 w-5 text-red-500 animate-pulse" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Connecting Elements */}
              <div className="hidden md:block">
                {/* SVG Path for first connection */}
                <svg className="absolute top-1/2 left-[29%] transform -translate-y-1/2 w-[8%] h-10" viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0,20 C30,5 70,35 100,20" stroke="currentColor" strokeWidth="2" className="text-primary/40 stroke-dasharray-1000 animate-pathDraw" />
                  <circle className="text-primary animate-slideRight" cx="50" cy="20" r="3" fill="currentColor" />
                </svg>

                {/* SVG Path for second connection */}
                <svg className="absolute top-1/2 right-[29%] transform -translate-y-1/2 w-[8%] h-10" viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0,20 C30,35 70,5 100,20" stroke="currentColor" strokeWidth="2" className="text-primary/40 stroke-dasharray-1000 animate-pathDraw" style={{ animationDelay: '0.5s' }} />
                  <circle className="text-primary animate-slideRight" cx="50" cy="20" r="3" fill="currentColor" style={{ animationDelay: '0.5s' }} />
                </svg>

                {/* Floating elements */}
                <div className="absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <Puzzle className="h-6 w-6 text-primary/30 animate-float" style={{ animationDelay: '1.2s' }} />
                </div>
                <div className="absolute bottom-10 left-1/4 transform -translate-x-1/2">
                  <Rocket className="h-5 w-5 text-primary/30 animate-float" style={{ animationDelay: '0.7s' }} />
                </div>
                <div className="absolute top-10 right-1/4 transform translate-x-1/2">
                  <Star className="h-4 w-4 text-amber-400/50 animate-float" style={{ animationDelay: '1.5s' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Why Us Reasons */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyUsReasons.map((reason, index) => (
              <div
                key={reason.title}
                className="bg-gradient-to-br from-white to-muted/10 p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-500 border border-muted/20 group animate-fadeIn relative overflow-hidden"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-primary/5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700 ease-in-out"></div>

                <div className="relative z-10">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 border border-primary/20 group-hover:border-primary/40 group-hover:bg-primary/20">
                    <reason.icon className="w-10 h-10 text-primary group-hover:text-primary/80 transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">{reason.title}</h3>
                  <p className="text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">{reason.description}</p>

                  <div className="mt-6 h-0 overflow-hidden opacity-0 group-hover:h-auto group-hover:opacity-100 transition-all duration-500 ease-in-out">
                    <div className="pt-4 border-t border-primary/20">
                      <div className="flex items-center text-primary">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        <span className="text-sm">Learn more</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-primary/90 to-indigo-600 text-white relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-indigo-400/10 rounded-full blur-3xl"></div>

        {/* Animated particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white/10 animate-float"
              style={{
                width: `${Math.random() * 40 + 10}px`,
                height: `${Math.random() * 40 + 10}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDuration: `${Math.random() * 10 + 15}s`,
                animationDelay: `${Math.random() * 5}s`
              }}
            ></div>
          ))}
        </div>

        <div className="container mx-auto max-w-6xl px-4 relative z-10">
          <div className="grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3 text-left">
              <Badge className="mb-6 bg-white/20 text-white hover:bg-white/30 border-none">Career Advancement</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Start Your Learning Journey?</h2>
              <p className="text-white/80 mb-8 text-lg">
                Find the perfect training program to advance your career. Our platform connects you with industry-leading courses
                designed to help you acquire in-demand skills and achieve your professional goals.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                {[
                  {
                    icon: Search,
                    title: "Find Your Path",
                    description: "Search from 1000+ courses across various industries and specializations"
                  },
                  {
                    icon: Target,
                    title: "Build Your Skills",
                    description: "Acquire the exact skills employers are looking for in your field"
                  },
                  {
                    icon: Rocket,
                    title: "Accelerate Your Career",
                    description: "Get certified and advance your professional journey"
                  },
                  {
                    icon: Users,
                    title: "Join a Community",
                    description: "Connect with peers and industry experts in your field"
                  }
                ].map((item, index) => (
                  <div key={item.title} className="flex items-start gap-3 group animate-fadeIn" style={{ animationDelay: `${index * 150}ms` }}>
                    <div className="mt-1 bg-white/10 p-2 rounded-full group-hover:bg-white/20 transition-colors duration-300 flex-shrink-0">
                      <item.icon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-base mb-1 group-hover:text-white/90 transition-colors duration-300">{item.title}</h4>
                      <p className="text-sm text-white/70 group-hover:text-white/80 transition-colors duration-300">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90 px-8 shadow-lg">
                  <Search className="mr-2 h-4 w-4" /> Find Training
                </Button>
                <Button size="lg" className="flex items-center gap-2 bg-white text-primary hover:bg-white/90">
                <Rocket className="mr-2 h-4 w-4" /> Build Your Career
                </Button>
              </div>
            </div>

            <div className="md:col-span-2 relative hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20 shadow-xl relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/0"></div>

                <div className="relative z-10">
                  <h3 className="text-xl font-bold mb-4">Find Your Perfect Training</h3>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-white/80 mb-1 block">What do you want to learn?</label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="e.g. Web Development, Data Science"
                          className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-4 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-transparent"
                        />
                        <Search className="absolute right-3 top-2.5 h-4 w-4 text-white/50" />
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-white/80 mb-1 block">Career Goal</label>
                      <select className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-transparent appearance-none">
                        <option value="" className="bg-primary text-white">Select your goal</option>
                        <option value="career-change" className="bg-primary text-white">Career Change</option>
                        <option value="skill-upgrade" className="bg-primary text-white">Skill Upgrade</option>
                        <option value="certification" className="bg-primary text-white">Professional Certification</option>
                        <option value="promotion" className="bg-primary text-white">Promotion/Advancement</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-white/80 mb-1 block">Experience Level</label>
                      <div className="flex gap-2">
                        {['Beginner', 'Intermediate', 'Advanced'].map(level => (
                          <label key={level} className="flex items-center bg-white/10 hover:bg-white/20 transition-colors duration-300 rounded-lg px-3 py-2 cursor-pointer">
                            <input type="radio" name="level" className="mr-2" />
                            <span className="text-sm">{level}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <Button className="w-full bg-white text-primary hover:bg-white/90 mt-2">
                      <Search className="mr-2 h-4 w-4" /> Find Courses
                    </Button>
                  </div>

                  <div className="mt-6 pt-6 border-t border-white/10 text-center">
                    <p className="text-sm text-white/70">Not sure where to start?</p>
                    <Button variant="link" className="text-white hover:text-white/80 p-0 h-auto mt-1">
                      Take our career assessment <ArrowRight className="ml-1 h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-16 bg-muted/10">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-md">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">Email Us</h3>
                <p className="text-muted-foreground mb-4">Have questions? Reach out to our support team.</p>
                <a href="mailto:support@learnhub.com" className="text-primary hover:underline">
                  support@learnhub.com
                </a>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <MapPin className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">Visit Us</h3>
                <p className="text-muted-foreground mb-4">Our headquarters are located in the heart of the city.</p>
                <address className="not-italic text-primary">
                  123 Education St, Suite 456<br />
                  San Francisco, CA 94103
                </address>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">Join Our Team</h3>
                <p className="text-muted-foreground mb-4">Interested in working with us? Check out our open positions.</p>
                <Button variant="outline" className="text-primary border-primary/20 hover:bg-primary/5">
                  View Careers
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
